import { faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';

import { Amount, CharityActivity, ParentProgrammeGeneralDetails, Programme } from '../../../../../types/entity.types';

import { ARCHIVED, UPCOMING } from '../../../../../utils/constants';
import { getClassNames, getWeekOptions } from '../../../../../utils/util';

import Button from '../../../../common/button/Button';
import Dropdown from '../../../../common/input/dropdown/Dropdown';

import { ReactComponent as CloseIcon } from '../../../../../resources/icons/close.svg';
import { ReactComponent as TrashIcon } from '../../../../../resources/icons/trash.svg';

import '../accountability.scss';
import './accountability-charity.scss';

type Props = {
  programme?: Programme;
  parentProgramme?: ParentProgrammeGeneralDetails;
  status: string;
  charityActivities: CharityActivity[];
  createCharityActivity: () => void;
  updateCharityActivity: (
    externalReference: string,
    key: keyof Omit<CharityActivity, 'amounts'>,
    value: string
  ) => void;
  deleteCharityActivity: (externalReference: string) => void;
  createCharityAmount: (activityExternalReference: string) => void;
  updateCharityAmount: (
    activityExternalReference: string,
    amountExternalReference: string,
    key: keyof Amount,
    value: string
  ) => void;
  deleteCharityAmount: (activityExternalReference: string, amountExternalReference: string) => void;
};

const AccountabilityCharity = ({
  programme,
  parentProgramme,
  status,
  charityActivities = [],
  createCharityActivity,
  updateCharityActivity,
  deleteCharityActivity,
  createCharityAmount,
  updateCharityAmount,
  deleteCharityAmount
}: Props) => {
  const [open, setOpen] = useState(true);

  const renderAmounts = (charityActivity: CharityActivity) => {
    const { externalReference, amounts } = charityActivity;

    const options = getWeekOptions(programme, parentProgramme);

    return (
      <div className="accountability-charity__charity-activity__amount">
        <div className="accountability-charity__charity-activity__amount__headers">
          <p>From Week:</p>
          <p>To Week:</p>
          <p>Daily Amount:</p>
        </div>
        {amounts.map((amount) => (
          <div
            className="accountability-charity__charity-activity__amount__inputs"
            key={`${externalReference}-${amount.externalReference}`}
            data-testid="charity-activity-amount"
          >
            <Dropdown
              value={{ label: amount.fromWeek, value: amount.fromWeek }}
              options={options.filter((option) => !amount.toWeek || Number(option.value) <= Number(amount.toWeek))}
              onChange={(option) =>
                updateCharityAmount(externalReference, amount.externalReference, 'fromWeek', option.value)
              }
              disabled={status === ARCHIVED}
            />
            <Dropdown
              value={{ label: amount.toWeek, value: amount.toWeek }}
              options={options.filter((option) => !amount.fromWeek || Number(option.value) >= Number(amount.fromWeek))}
              onChange={(option) =>
                updateCharityAmount(externalReference, amount.externalReference, 'toWeek', option.value)
              }
              disabled={status === ARCHIVED}
            />
            <div>
              <input
                className="accountability-charity__input"
                value={amount.amount}
                onChange={({ target }) =>
                  updateCharityAmount(externalReference, amount.externalReference, 'amount', target.value)
                }
                disabled={status === ARCHIVED}
              />
              {status !== ARCHIVED && (
                <FontAwesomeIcon
                  className="accountability-charity__charity-activity__amount__add-icon"
                  icon={faPlus}
                  onClick={() => createCharityAmount(externalReference)}
                  data-testid="create-charity-amount-icon"
                />
              )}
              {status !== ARCHIVED && amounts.length > 1 && (
                <TrashIcon
                  className="accountability-charity__charity-activity__amount__trash-icon"
                  onClick={() => deleteCharityAmount(externalReference, amount.externalReference)}
                  data-testid="delete-charity-amount-icon"
                />
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderCharityActivities = () => {
    return (
      <div className="accountability-charity__charity-activities">
        {charityActivities.map((charityActivity, index) => (
          <div className="accountability-charity__charity-activity" key={index} data-testid="charity-activity">
            <div className="accountability-charity__charity-activity__headers">
              <p>Type of Charity:</p>
              <p>Measurement:</p>
            </div>
            <div className="accountability-charity__charity-activity__inputs">
              <input
                className="accountability-charity__input"
                value={charityActivity.name}
                onChange={({ target }) =>
                  updateCharityActivity(charityActivity.externalReference, 'name', target.value)
                }
                disabled={status === ARCHIVED}
              />
              <input
                className="accountability-charity__input"
                value={charityActivity.measurement}
                onChange={({ target }) =>
                  updateCharityActivity(charityActivity.externalReference, 'measurement', target.value)
                }
                disabled={status === ARCHIVED}
              />
            </div>
            {renderAmounts(charityActivity)}
            {status === UPCOMING && charityActivities.length > 1 && (
              <div className="accountability-charity__charity-activity__close-icon">
                <CloseIcon
                  className="accountability-charity__close-icon"
                  onClick={() => deleteCharityActivity(charityActivity.externalReference)}
                  data-testid="delete-charity-activity-icon"
                />
              </div>
            )}
          </div>
        ))}
        {status !== ARCHIVED && <Button text="Add" onClick={createCharityActivity} />}
      </div>
    );
  };

  return (
    <div
      className={getClassNames('accountability__section', {
        closed: !open
      })}
      data-testid="accountability-charity"
    >
      <div className="accountability__section__header">
        <p>CHARITY WORK</p>
        {!open && (
          <FontAwesomeIcon
            className="accountability__section__open-icon"
            icon={faPlus}
            onClick={() => setOpen(true)}
            data-testid="open-icon"
          />
        )}
        {open && (
          <FontAwesomeIcon
            className="accountability__section__close-icon"
            icon={faMinus}
            onClick={() => setOpen(false)}
            data-testid="close-icon"
          />
        )}
      </div>
      {open && <div className="accountability-charity">{renderCharityActivities()}</div>}
    </div>
  );
};

export default AccountabilityCharity;
